import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import './LegalPrivate.css';

const PrivacyPrivate: React.FC = () => {
    const [showDropdown, setShowDropdown] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setShowDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <div className="legal-private-container">
            <div className="legal-private-card">
                <div className="legal-private-header">
                    <button className="legal-private-back-button" onClick={() => navigate(-1)}>
                        <ArrowLeft className="legal-private-back-icon" />
                    </button>
                    <h1 className="legal-private-title">Privacy Policy</h1>
                    {!location.pathname.startsWith('/legal') && (
                        <div className="legal-private-nav-dropdown" ref={dropdownRef}>
                            <button className="legal-private-nav-button" onClick={() => setShowDropdown(!showDropdown)}>
                                <span className="legal-private-nav-text">Privacy Policy</span>
                                <div className={`legal-private-nav-icon ${showDropdown ? 'open' : ''}`}>
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="6,9 12,15 18,9"></polyline>
                                    </svg>
                                </div>
                            </button>

                            {showDropdown && (
                                <div className="legal-private-nav-menu">
                                    <button className="legal-private-nav-item" onClick={() => window.location.href = '/terms'}>
                                        <span>Terms of Use</span>
                                    </button>
                                    <button className="legal-private-nav-item active" onClick={() => window.location.href = '/privacy'}>
                                        <span>Privacy Policy</span>
                                        <svg className="legal-private-nav-check" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="20,6 9,17 4,12"></polyline>
                                        </svg>
                                    </button>
                                    <button className="legal-private-nav-item" onClick={() => window.location.href = '/community-guidelines'}>
                                        <span>Community Guidelines</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="legal-private-content">
                    <section className="legal-private-section">
                        <p>
                            Mindcast ("we," "us," or "our") respects your privacy. This Privacy Policy explains how we collect, use, store, and protect information when you use Mindcast (the "Service"). By using the Service, you consent to this Privacy Policy. If you do not agree, do not use the Service.
                        </p>
                    </section>

                    <section className="legal-private-section">
                        <h2>1. Information We Collect</h2>
                        <p>We may collect information directly or indirectly when you use the Service, including:</p>
                        <ul>
                            <li><strong>Account Information:</strong> Name, email, age, profile details, and other information provided during registration or profile updates.</li>
                            <li><strong>Content & Activity:</strong> Posts, comments, likes, shares, reviews, and other interactions with the Service.</li>
                            <li><strong>Device & Usage Information:</strong> Device type, operating system, IP address, browser, app version, login times, and activity logs.</li>
                            <li><strong>Third-Party Integrations & APIs:</strong> Information accessed, retrieved, or shared via APIs and third-party services (e.g., books, podcasts), including metadata, ratings, and interactions with content.</li>
                            <li><strong>Automatically Collected Data:</strong> Cookies, analytics data, crash reports, and other telemetry data for improving performance, security, and user experience.</li>
                        </ul>
                        <p>We may combine this information with other data to provide, improve, or secure the Service.</p>
                    </section>

                    <section className="legal-private-section">
                        <h2>2. How We Use Your Information</h2>
                        <p>We may use your information to:</p>
                        <ul>
                            <li>Provide, operate, maintain, and improve Mindcast.</li>
                            <li>Personalize content and recommendations.</li>
                            <li>Monitor compliance with our Terms, detect fraud, abuse, or misuse, and protect the community.</li>
                            <li>Respond to inquiries, complaints, or legal requests.</li>
                            <li>Conduct research, analytics, and development to improve the Service.</li>
                            <li>Aggregate, anonymize, or de-identify data for any lawful purpose, including business analysis, marketing, and reporting.</li>
                        </ul>
                    </section>

                    <section className="legal-private-section">
                        <h2>3. Sharing & Disclosure</h2>
                        <p>We do not sell your personal information. We may share information in the following circumstances:</p>
                        <ul>
                            <li><strong>Service Providers:</strong> With vendors, contractors, or partners performing services on our behalf and bound to confidentiality.</li>
                            <li><strong>Legal Compliance:</strong> When required by law, court order, subpoena, or other legal process, or to protect Mindcast, its users, or third parties.</li>
                            <li><strong>Protection of Rights and Safety:</strong> To prevent fraud, security incidents, or potential harm to the Service, users, or third parties.</li>
                            <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, sale of assets, or bankruptcy.</li>
                        </ul>
                        <p>We are not responsible for how third parties use your information, including API providers, social media services, or other integrated platforms.</p>
                    </section>

                    <section className="legal-private-section">
                        <h2>4. User Content & Responsibility</h2>
                        <ul>
                            <li>Users are solely responsible for the content they post or share.</li>
                            <li>All content posted by users represents their own opinions, thoughts, and experiences, not those of Mindcast. Mindcast does not endorse, verify, or assume responsibility for user content.</li>
                            <li>Mindcast may remove, restrict, or modify content at its sole discretion if it violates our Terms, community standards, or applicable law.</li>
                            <li>Mindcast is not responsible for any damages arising from reliance on user content or interactions with other users.</li>
                        </ul>
                    </section>

                    <section className="legal-private-section">
                        <h2>5. Data Security & Retention</h2>
                        <ul>
                            <li>We implement reasonable administrative, technical, and physical safeguards to protect your information.</li>
                            <li>No system is completely secure; Mindcast cannot guarantee absolute protection against unauthorized access, loss, or misuse.</li>
                            <li>Personal information is retained only as long as necessary for the purposes described in this Privacy Policy or to comply with legal obligations.</li>
                            <li>Data may be stored in backups for up to 90 days after deletion.</li>
                        </ul>
                    </section>

                    <section className="legal-private-section">
                        <h2>6. Third-Party Content & APIs</h2>
                        <ul>
                            <li>Mindcast may access or display content from third-party services and APIs (e.g., books, podcasts).</li>
                            <li>Information about your interactions with third-party content (e.g., books or podcasts viewed, rated, or commented on) may be collected by these third parties or shared with Mindcast.</li>
                            <li>Mindcast is not responsible for third-party privacy practices, data handling, or content availability. Users access third-party services at their own risk.</li>
                            <li>Mindcast retains all rights to enforce restrictions against scraping, automated data collection, or misuse of APIs.</li>
                        </ul>
                    </section>

                    <section className="legal-private-section">
                        <h2>7. International Users</h2>
                        <p>Mindcast is based in Canada. By using the Service, you consent to the transfer, storage, and processing of your information in Canada and any other jurisdictions where Mindcast operates or hosts data.</p>
                        <p>Users outside Canada are responsible for compliance with local laws. Mindcast makes no representation regarding enforceability of this Privacy Policy in other jurisdictions.</p>
                    </section>

                    <section className="legal-private-section">
                        <h2>8. Children's Privacy</h2>
                        <ul>
                            <li>Mindcast is not intended for children under 16.</li>
                            <li>We do not knowingly collect information from anyone under this age. If we learn we have collected such information, we will delete it promptly.</li>
                        </ul>
                    </section>

                    <section className="legal-private-section">
                        <h2>9. Updates to This Privacy Policy</h2>
                        <ul>
                            <li>We may update this Privacy Policy at any time.</li>
                            <li>Significant changes will be communicated. Continued use of Mindcast constitutes acceptance of the updated policy.</li>
                        </ul>
                    </section>

                    <section className="legal-private-section">
                        <h2>10. Disclaimers & Limitation of Liability</h2>
                        <ul>
                            <li>Mindcast makes no guarantees regarding privacy, security, or confidentiality of information.</li>
                            <li>To the fullest extent permitted by law, Mindcast is not liable for damages arising from use of the Service, disclosure of information, or reliance on user or third-party content.</li>
                            <li>Users assume all risk when accessing the Service.</li>
                        </ul>
                    </section>
                </div>

                <div className="legal-private-footer">
                    <p>By using Mindcast, you acknowledge that you have read and understood this privacy policy.</p>
                    <p className="legal-private-subtitle">Last Updated: September 27th, 2025</p>
                </div>
            </div>
        </div>
    );
};

export default PrivacyPrivate;


